import { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { 
  getCommissionSum, 
  searchCommissions,
  searchSettlements 
} from '../../../Module/Settlement/Presentation/settlementActions';
import {
  selectCommissionSumStatus,
  selectCommissionSumExtra,
  selectCommissionSearchStatus,
  selectCommissionSearchResults,
  selectCommissionSearchMeta,
  selectSettlementSearchStatus,
  selectSettlementSearchResults,
} from '../../../Module/Settlement/Presentation/settlementSelectors';
import { clearCommissionSum, clearCommissionSearch, clearSearchResults } from '../../../Module/Settlement/Presentation/settlementSlice';
import { selectSelectedProject, selectProjects } from '../../../Module/Project/Presentation/projectSelectors';
import { getCloudFrontImageUrl } from '../../../Utils/cloudFrontImage';
import type { 
  CommissionSumExtra, 
  CommissionSearchItem, 
  CommissionSearchMeta,
  SettlementItem,
} from '../../../Module/Settlement/Domain/entity/SettlementTypes';

export type PaymentSummary = {
  promisedAmount: number;      // 약정 후원금액 (결제실패 + 결제완료)
  failedAmount: number;        // 결제실패 금액 (음수)
  completedAmount: number;     // 결제완료 금액
  fees: number;                // 결제 수수료 (음수)
  missionFund: number;         // 세계 선교기금 (음수)
  finalDeposit: number;        // 입금액
  year: number;
  month: number | 'all';
};

export type PaymentMethod = 'cms' | 'card';
export type PaymentStatusCode = 'complete' | 'fail';

export type PaymentDetail = {
  id: string;
  name: string;
  method: PaymentMethod;
  status: PaymentStatusCode;
  email: string;
  phone: string;
  date: string;
  message: string;
  amount: number;
  fee: number;
  profileImg: string;
  projectTitle?: string;
  donationType?: string;
  paymentDescription?: string;
};

// 날짜 범위 계산 유틸리티
const getDateRange = (year: number, month: number | 'all') => {
  if (month === 'all') {
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`
    };
  }
  const monthStr = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: `${year}-${monthStr}-01`,
    endDate: `${year}-${monthStr}-${lastDay}`
  };
};

// pay_type을 한글로 변환
const getPaymentMethod = (payType: string): PaymentMethod => {
  const lowerType = payType?.toLowerCase() ?? '';
  if (lowerType.includes('card') || lowerType.includes('thebill')) {
    return 'card';
  }
  return 'cms';
};

const formatDisplayDate = (rawDate?: string) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (Number.isNaN(dateObj.getTime())) return '';
  return `${dateObj.getFullYear()}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${String(dateObj.getDate()).padStart(2, '0')}`;
};

export const usePaymentStatus = (
  selectedYear: number,
  selectedMonth: number | 'all',
  targetProjectId?: string
) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();

  // Project 정보 가져오기
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  
  // 전달받은 ID가 있으면 그것을, 없으면 선택된 프로젝트나 첫 번째 프로젝트 사용
    const projectId = useMemo(() => {
    if (targetProjectId) return targetProjectId;

    if (selectedProject?.project_id) {
      return selectedProject.project_id;
    }
    if (projects.length > 0 && projects[0].project_id) {
      return projects[0].project_id;
    }
    return null;
  }, [targetProjectId, selectedProject, projects]);

  const currentProject = useMemo(() => {
    if (!projectId) return undefined;
    if (selectedProject?.project_id === projectId || selectedProject?._id === projectId) {
      return selectedProject;
    }
    return projects.find(p => p.project_id === projectId || p._id === projectId);
  }, [projectId, projects, selectedProject]);

  // Settlement API 상태 (타입 명시)
  const summaryStatus = useAppSelector(selectCommissionSumStatus);
  const summaryExtra = useAppSelector(selectCommissionSumExtra) as CommissionSumExtra | undefined;
  const settlementSearchStatus = useAppSelector(selectSettlementSearchStatus);
  const settlementSearchResults = useAppSelector(selectSettlementSearchResults) as SettlementItem[] | undefined;
  
  const detailsStatus = useAppSelector(selectCommissionSearchStatus);
  const detailsResults = useAppSelector(selectCommissionSearchResults) as CommissionSearchItem[] | undefined;
  const detailsMeta = useAppSelector(selectCommissionSearchMeta) as CommissionSearchMeta | undefined;

  /** 2025년 7월 이후: POST /settlement/m/sch 사용, 이전: POST /mgmtcommission/m/sum 사용 */
  const isNewSettlementPeriod = useMemo(() => {
    if (selectedYear > 2025) return true;
    if (selectedYear === 2025 && selectedMonth !== 'all' && selectedMonth >= 7) return true;
    return false;
  }, [selectedYear, selectedMonth]);

  // 합계 데이터 가져오기
  const fetchSummary = useCallback(() => {
    if (!projectId) return;

    if (isNewSettlementPeriod) {
      // POST /settlement/m/sch (2025-07~)
      const params: { fld: string; val: string | number; op: string }[] = [
        { fld: 'project_id', val: projectId, op: 'eq' },
        { fld: 'settle_year', val: selectedYear, op: 'eq' },
      ];
      if (selectedMonth !== 'all') {
        params.push({ fld: 'settle_month', val: selectedMonth, op: 'eq' });
      }
      dispatch(searchSettlements({ params }));
    } else {
      // POST /mgmtcommission/m/sum (2025-06 이전)
      const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
      dispatch(getCommissionSum({
        limit: 1,
        params: [
          { fld: 'projectId', val: projectId, op: 'eq' },
          { fld: 'pay_date', op: 'between', val: startDate, v2: endDate }
        ],
        local_field: 'projectId',
        from: 'projects'
      }));
    }
  }, [dispatch, projectId, selectedYear, selectedMonth, isNewSettlementPeriod]);

  // 상세 내역 가져오기
  const fetchDetails = useCallback(() => {
    if (!projectId) return;

    const { startDate, endDate } = getDateRange(selectedYear, selectedMonth);
    
    dispatch(searchCommissions({
      params: [
        { fld: 'projectId', val: projectId, op: 'eq' },
        { fld: 'pay_date', op: 'between', val: startDate, v2: endDate }
      ],
      local_field: 'projectId',
      from: 'projects',
      sort: 'pay_date:desc',
      limit: 100
    }));
  }, [dispatch, projectId, selectedYear, selectedMonth]);

  // 날짜 변경 시 데이터 새로 가져오기
  useEffect(() => {
    if (projectId) {
      fetchSummary();
      fetchDetails();
    }
  }, [projectId, selectedYear, selectedMonth, fetchSummary, fetchDetails]);

  // 정리
  useEffect(() => {
    return () => {
      dispatch(clearCommissionSum());
      dispatch(clearCommissionSearch());
      dispatch(clearSearchResults());
    };
  }, [dispatch]);

  // API 데이터를 UI 형식으로 변환
  const summary: PaymentSummary = useMemo(() => {
    const accumulatedAmount = (currentProject?.accumulated_amount ?? 0) as number;
    const supportSupportPercent = Number((currentProject?.support_project as { support_percent?: number })?.support_percent ?? 0);

    if (isNewSettlementPeriod) {
      // 2025-07~: POST /settlement/m/sch
      const items = settlementSearchResults ?? [];
      const incomeSum = items.reduce((s, i) => s + (i.income_sum ?? 0), 0);
      const supportSum = items.reduce((s, i) => s + (i.support_sum ?? 0), 0);
      const commissionSum =
        (items.reduce((s, i) => s + (i.kr_card_commission_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.kr_cms_commission_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.us_card_commission_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.us_card_mgmt_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.bank_manage_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.card_manage_sum ?? 0), 0) +
          items.reduce((s, i) => s + (i.exchange_manage_sum ?? 0), 0));
      const sumRealAmount = items.reduce((s, i) => s + (i.sum_real_amount ?? i.netincome_sum ?? 0), 0);

      const promisedAmount = accumulatedAmount;
      const completedAmount = incomeSum;
      const failedAmount = promisedAmount - completedAmount;
      const fees = -commissionSum;
      const missionFund = -supportSum;
      const finalDeposit = sumRealAmount;

      return {
        promisedAmount,
        failedAmount,
        completedAmount,
        fees,
        missionFund,
        finalDeposit,
        year: selectedYear,
        month: selectedMonth
      };
    }

    // 2025-06 이전: POST /mgmtcommission/m/sum
    if (!summaryExtra) {
      return {
        promisedAmount: accumulatedAmount,
        failedAmount: 0,
        completedAmount: 0,
        fees: 0,
        missionFund: 0,
        finalDeposit: 0,
        year: selectedYear,
        month: selectedMonth
      };
    }

    const sumRealAmount = summaryExtra.sum_real_amount ?? 0;
    const sumManageCommission = summaryExtra.sum_manage_commission ?? 0;
    const sumCommission = summaryExtra.sum_commission ?? 0;
    const sumExchangeManageCommission = summaryExtra.sum_exchange_manage_commission ?? 0;

    const completedAmount = sumRealAmount + sumManageCommission + sumCommission + sumExchangeManageCommission;
    const promisedAmount = accumulatedAmount;
    const failedAmount = promisedAmount - completedAmount;
    const fees = -(sumManageCommission + sumCommission + sumExchangeManageCommission);

    let missionFund: number;
    if (supportSupportPercent > 99) {
      missionFund = supportSupportPercent;
    } else {
      missionFund = (sumRealAmount * supportSupportPercent) / 100;
    }
    // UI에서 negative로 표시하므로 양수로 저장
    const finalDeposit = sumRealAmount - missionFund;

    return {
      promisedAmount,
      failedAmount,
      completedAmount,
      fees,
      missionFund: -missionFund, // UI negative 표시용
      finalDeposit,
      year: selectedYear,
      month: selectedMonth
    };
  }, [
    summaryExtra,
    settlementSearchResults,
    isNewSettlementPeriod,
    currentProject,
    selectedYear,
    selectedMonth
  ]);

  // 상세 내역 변환
  const details: PaymentDetail[] = useMemo(() => {
    if (!detailsResults || detailsResults.length === 0) {
      return [];
    }

    const sorted = [...detailsResults].sort((a, b) => {
      const da = a.pay_date ?? '';
      const db = b.pay_date ?? '';
      if (da !== db) return db.localeCompare(da);
      return (a.sponsor_name ?? '').localeCompare(b.sponsor_name ?? '', 'ko');
    });

    return sorted.map((item, index) => {
      // 프로젝트 타이틀 추출
      const projectTitle = item.subm?.[0]?.title?.replace(/<br\s*\/?>/gi, ' ') ?? '';
      
      // 결제 상세·엑셀: API pay_date 기준 (mgmtcommission/m/sch)
      const formattedDate = formatDisplayDate(item.pay_date);

      const isOneTime = ['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes((currentProject?.type ?? '').toUpperCase());
      const donationType = isOneTime ? t('page.newProject.review.fundingType.once') : t('page.newProject.review.fundingType.regular');
      const paymentDescription = item.pay_result_comment || item.result_msg || item.comment || '';

      return {
        id: item.mgmt_commission_id ?? `item-${index}`,
        name: item.sponsor_name ?? t('common.labels.anonymous'),
        method: getPaymentMethod(item.pay_type),
        status: item.real_amount > 0 ? 'complete' : 'fail',
        email: item.email ?? '',
        phone: item.sponsor_tel ?? '',
        date: formattedDate,
        message: item.comment ?? item.pay_result_comment ?? '',
        amount: item.real_amount ?? 0,
        fee: item.commission ?? 0,
        profileImg: item.thumbnail ? (getCloudFrontImageUrl(item.thumbnail, '84x84') || item.thumbnail) : '',
        projectTitle,
        donationType,
        paymentDescription,
      } as PaymentDetail;
    });
  }, [detailsResults, t, currentProject]);

  // 데이터 존재 여부
  const hasData = useMemo(() => {
    if (isNewSettlementPeriod) {
      return details.length > 0 || (settlementSearchResults?.length ?? 0) > 0;
    }
    return details.length > 0 || (summaryExtra?.project_count ?? 0) > 0;
  }, [isNewSettlementPeriod, details.length, settlementSearchResults, summaryExtra?.project_count]);

  // 로딩 상태
  const isLoading = useMemo(() => {
    if (isNewSettlementPeriod) {
      return settlementSearchStatus === 'searching' || detailsStatus === 'searching';
    }
    return summaryStatus === 'loading' || detailsStatus === 'searching';
  }, [isNewSettlementPeriod, settlementSearchStatus, summaryStatus, detailsStatus]);

  /** 현재 기간에 맞는 summary 로딩 상태 (idle/loading 등) */
  const effectiveSummaryStatus = isNewSettlementPeriod
    ? (settlementSearchStatus === 'searching' ? 'loading' : settlementSearchStatus === 'searched' ? 'loaded' : 'idle')
    : summaryStatus;

  // 페이지네이션 정보
  const pagination = useMemo(() => ({
    totalElements: detailsMeta?.totalElements ?? 0,
    totalPages: detailsMeta?.totalPages ?? 0,
    currentPage: Math.floor((detailsMeta?.msearch?.offset ?? 0) / (detailsMeta?.perPage ?? 10)) + 1,
    perPage: detailsMeta?.perPage ?? 10
  }), [detailsMeta]);

  /** 달러(USD)인 경우 /1000 해서 표시 */
  const isUsd = (currentProject?.currency_type ?? 'KRW') === 'USD';
  const displayDivisor = isUsd ? 1000 : 1;

  return {
    // 데이터
    summary,
    details,
    hasData,
    
    // 상태
    isLoading,
    summaryStatus: effectiveSummaryStatus,
    detailsStatus,
    
    // 페이지네이션
    pagination,
    
    // 액션
    fetchSummary,
    fetchDetails,
    
    // 프로젝트 정보
    projectId,
    currentProject,
    /** 달러일 때 표시용 나눗셈 (1000), 원화면 1 */
    displayDivisor,
    isUsd,
  };
};

export default usePaymentStatus;
